import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ColumnLayout,
  ExpandableSection,
  FileInput,
  FileInputProps,
  Flashbar,
  FlashbarProps,
  FormField,
  Input,
  Modal,
  Select,
  SpaceBetween,
  Spinner,
  Table,
  TableProps,
  Textarea,
  TextFilter,
} from '@cloudscape-design/components';
import yaml from 'js-yaml';
import {
  getUpgradeTemplateSelections,
  updateUpgradeTemplateSelections,
  useGetUpgradeTemplateSelections,
} from 'api/admin';
import { UpgradeSelections } from 'api/admin-models';
import { handleUpgradeError } from 'src/utils/error-handling';

interface PublicReasonNote {
  note?: string;
  public?: boolean;
}

interface VersionReason {
  upgrade_recommended?: boolean;
  reasons?: PublicReasonNote[] | null;
}

interface RecommendationRecord {
  selected?: boolean;
  recommended_on?: string;
  recommended_by?: string;
  upgrade_recommended?: boolean;
  recommended_version?: string;
  reasons?: Record<string, VersionReason> | null;
}

const REASON_COLUMNS = [
  { id: 'eol', header: 'EOL', key: 'eol', kind: 'boolean' },
  { id: 'eolReason', header: 'EOL reason', key: 'eol', kind: 'reason' },
  {
    id: 'incompatibleTargetKube',
    header: 'Incompatible target kube',
    key: 'incompatible.target.kube',
    kind: 'boolean',
  },
  {
    id: 'incompatibleTargetKubeReason',
    header: 'Incompatible target kube reason',
    key: 'incompatible.target.kube',
    kind: 'reason',
  },
  { id: 'outdated', header: 'Outdated', key: 'outdated', kind: 'boolean' },
  {
    id: 'outdatedReason',
    header: 'Outdated reason',
    key: 'outdated',
    kind: 'reason',
  },
] as const;

interface VersionSelectionFields {
  created_on?: string;
  created_by?: string;
  last_modified_on?: string;
  last_modified_by?: string;
  version?: string;
  selected_version?: string;
  should_upgrade?: boolean;
  recommendations?: RecommendationRecord[] | null;
}

export interface ProjectVersionRow extends VersionSelectionFields {
  project?: string;
}

interface SelectionsDocument {
  id?: string;
  account?: string;
  upgrade_type?: string;
  created_on?: string;
  created_by?: string;
  last_modified_on?: string;
  last_modified_by?: string;
  project_versions?: ProjectVersionRow[];
  project_component_versions?: (VersionSelectionFields & {
    project_component?: string;
  })[];
  package_versions?: (VersionSelectionFields & { package?: string })[];
}

function extractProjectVersions(doc: SelectionsDocument): ProjectVersionRow[] {
  return Array.isArray(doc.project_versions) ? doc.project_versions : [];
}

function projectName(item: ProjectVersionRow): string {
  return String(item.project ?? '');
}

function versionOf(item: ProjectVersionRow): string {
  return String(item.version ?? '');
}

function selectedVersionOf(item: ProjectVersionRow): string {
  return String(item.selected_version ?? '');
}

function shouldUpgradeOf(item: ProjectVersionRow): boolean {
  return Boolean(item.should_upgrade);
}

function recommendationsOf(item: ProjectVersionRow): RecommendationRecord[] {
  return Array.isArray(item.recommendations) ? item.recommendations : [];
}

function selectedRecommendation(
  item: ProjectVersionRow,
): RecommendationRecord | undefined {
  const recommendations = recommendationsOf(item);
  if (recommendations.length === 0) {
    return undefined;
  }
  return (
    recommendations.find((recommendation) => recommendation.selected) ??
    recommendations[0]
  );
}

function recommendedVersionValue(item: ProjectVersionRow): string {
  const selected = selectedRecommendation(item);
  return String(selected?.recommended_version ?? '');
}

function recommendedVersionOf(item: ProjectVersionRow): string {
  return recommendedVersionValue(item) || '-';
}

function applyRecommendedVersion(item: ProjectVersionRow, newValue: unknown) {
  const value = String(newValue ?? '');
  const recommendations = recommendationsOf(item);
  let selected =
    recommendations.find((recommendation) => recommendation.selected) ??
    recommendations[0];
  if (!selected) {
    selected = { selected: true, recommended_version: value };
    item.recommendations = [selected];
    return;
  }
  selected.recommended_version = value;
}

function reasonDetail(
  item: ProjectVersionRow,
  reasonKey: string,
): VersionReason | undefined {
  const reasons = selectedRecommendation(item)?.reasons;
  if (!reasons) {
    return undefined;
  }
  return reasons[reasonKey];
}

function publicReasonNotes(detail: VersionReason | undefined): string[] {
  if (!detail?.reasons) {
    return [];
  }
  return detail.reasons
    .filter((reason) => reason.public && reason.note)
    .map((reason) => String(reason.note));
}

function publicReasonText(detail: VersionReason | undefined): string {
  return publicReasonNotes(detail).join('\n');
}

function setPublicReasonNote(
  notes: PublicReasonNote[] | null | undefined,
  note: string,
): PublicReasonNote[] {
  const current = notes ? [...notes] : [];
  const publicIndex = current.findIndex((entry) => entry.public);
  if (!note) {
    return current.filter((entry) => !entry.public);
  }
  if (publicIndex >= 0) {
    const updated = { ...current[publicIndex] };
    updated.note = note;
    updated.public = true;
    current[publicIndex] = updated;
    return current.filter(
      (entry, index) => !entry.public || index === publicIndex,
    );
  }
  return [...current, { note: note, public: true }];
}

function reasonColumn(columnId: string | undefined) {
  return REASON_COLUMNS.find((column) => column.id === columnId);
}

function applyReasonEdit(
  item: ProjectVersionRow,
  columnId: string,
  newValue: unknown,
) {
  const column = reasonColumn(columnId);
  if (!column) {
    return;
  }
  const recommendations = recommendationsOf(item);
  let selected =
    recommendations.find((recommendation) => recommendation.selected) ??
    recommendations[0];
  if (!selected) {
    selected = { selected: true, reasons: {} };
    item.recommendations = [selected];
  }
  if (!selected.reasons) {
    selected.reasons = {};
  }
  const detail = selected.reasons[column.key] ?? {
    upgrade_recommended: false,
    reasons: [],
  };
  if (column.kind === 'boolean') {
    detail.upgrade_recommended = Boolean(newValue);
  } else {
    detail.reasons = setPublicReasonNote(
      detail.reasons,
      String(newValue ?? ''),
    );
  }
  selected.reasons[column.key] = detail;
}

interface ReasonDraft {
  upgradeRecommended?: boolean;
  note: string;
}

interface SelectionDraft {
  version: string;
  selectedVersion: string;
  shouldUpgrade: boolean;
  recommendedVersion: string;
  reasons: Record<string, ReasonDraft>;
}

const YES_NO_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

function yesNoOption(value: boolean | undefined) {
  if (value === undefined) {
    return null;
  }
  return value
    ? { label: 'Yes', value: 'true' }
    : { label: 'No', value: 'false' };
}

function reasonDraftFor(
  item: ProjectVersionRow,
  reasonKey: string,
): ReasonDraft {
  const detail = reasonDetail(item, reasonKey);
  return {
    upgradeRecommended: detail
      ? Boolean(detail.upgrade_recommended)
      : undefined,
    note: publicReasonText(detail),
  };
}

function draftFromRow(item: ProjectVersionRow): SelectionDraft {
  const reasons: Record<string, ReasonDraft> = {};
  for (const column of REASON_COLUMNS) {
    if (column.kind === 'boolean') {
      reasons[column.key] = reasonDraftFor(item, column.key);
    }
  }
  return {
    version: versionOf(item),
    selectedVersion: selectedVersionOf(item),
    shouldUpgrade: shouldUpgradeOf(item),
    recommendedVersion: recommendedVersionValue(item),
    reasons: reasons,
  };
}

function applyEdit(
  item: ProjectVersionRow,
  columnId: string | undefined,
  newValue: unknown,
) {
  if (columnId === 'version') {
    item.version = String(newValue ?? '');
  } else if (columnId === 'selectedVersion') {
    const value = String(newValue ?? '');
    if (value) {
      item.selected_version = value;
    } else {
      delete item.selected_version;
    }
  } else if (columnId === 'shouldUpgrade') {
    item.should_upgrade = Boolean(newValue);
  } else if (columnId === 'recommendedVersion') {
    applyRecommendedVersion(item, newValue);
  } else if (columnId && reasonColumn(columnId)) {
    applyReasonEdit(item, columnId, newValue);
  }
}

function applyDraft(item: ProjectVersionRow, draft: SelectionDraft): boolean {
  let changed = false;
  if (versionOf(item) !== draft.version) {
    applyEdit(item, 'version', draft.version);
    changed = true;
  }
  if (selectedVersionOf(item) !== draft.selectedVersion) {
    applyEdit(item, 'selectedVersion', draft.selectedVersion);
    changed = true;
  }
  if (shouldUpgradeOf(item) !== draft.shouldUpgrade) {
    applyEdit(item, 'shouldUpgrade', draft.shouldUpgrade);
    changed = true;
  }
  if (recommendedVersionValue(item) !== draft.recommendedVersion) {
    applyEdit(item, 'recommendedVersion', draft.recommendedVersion);
    changed = true;
  }
  for (const column of REASON_COLUMNS) {
    if (column.kind !== 'boolean') {
      continue;
    }
    const reason = draft.reasons[column.key];
    if (!reason) {
      continue;
    }
    const current = reasonDraftFor(item, column.key);
    if (
      reason.upgradeRecommended !== undefined &&
      reason.upgradeRecommended !== current.upgradeRecommended
    ) {
      applyEdit(item, column.id, reason.upgradeRecommended);
      changed = true;
    }
    const noteColumn = REASON_COLUMNS.find(
      (entry) => entry.key === column.key && entry.kind === 'reason',
    );
    if (noteColumn && reason.note !== current.note) {
      applyEdit(item, noteColumn.id, reason.note);
      changed = true;
    }
  }
  return changed;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function auditFields(row: VersionSelectionFields) {
  return {
    created_on: text(row.created_on),
    created_by: text(row.created_by),
    last_modified_on: text(row.last_modified_on),
    last_modified_by: text(row.last_modified_by),
  };
}

function versionSelection(row: VersionSelectionFields) {
  const selectedVersion = text(row.selected_version);
  return {
    ...auditFields(row),
    should_upgrade: Boolean(row.should_upgrade),
    version: text(row.version),
    ...(selectedVersion ? { selected_version: selectedVersion } : {}),
    ...(Array.isArray(row.recommendations)
      ? { recommendations: row.recommendations }
      : {}),
  };
}

// GET and PUT both use the snake_case selections document. Empty
// selected_version and null recommendations are omitted, matching the API.
export function selectionsDownloadBody(doc: unknown) {
  const document = doc as SelectionsDocument;
  return toSelectionsYamlBody(document, extractProjectVersions(document));
}

function toSelectionsYamlBody(
  doc: SelectionsDocument,
  projectRows: ProjectVersionRow[],
) {
  return {
    ...auditFields(doc),
    id: text(doc.id),
    account: text(doc.account),
    upgrade_type: text(doc.upgrade_type),
    project_versions: projectRows.map((row) => ({
      project: projectName(row),
      ...versionSelection(row),
    })),
    project_component_versions: (doc.project_component_versions ?? []).map(
      (row) => ({
        project_component: text(row.project_component),
        ...versionSelection(row),
      }),
    ),
    package_versions: (doc.package_versions ?? []).map((row) => ({
      package: text(row.package),
      ...versionSelection(row),
    })),
  };
}

function readApiMessage(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(trimmed) as { message?: unknown };
      if (typeof parsed.message === 'string' && parsed.message.trim()) {
        return parsed.message.trim();
      }
    } catch {
      return trimmed;
    }
    return undefined;
  }
  if (data && typeof data === 'object' && 'message' in data) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
  }
  return undefined;
}

function isNotFound(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return false;
  }
  return (error as { response?: { status?: number } }).response?.status === 404;
}

function selectionsLoadMessage(error: unknown): string {
  return (
    readApiMessage(error) ||
    (isNotFound(error) ? 'Selections not found' : 'Unable to load selections.')
  );
}

function saveErrorMessage(error: unknown): string {
  const apiMessage = readApiMessage(error);
  if (apiMessage) {
    return `Failed to update selections: ${apiMessage}`;
  }
  if (error instanceof Error && error.message) {
    return `Failed to update selections: ${error.message}`;
  }
  return 'Failed to update selections.';
}

function sortValue(item: ProjectVersionRow, field: string | undefined): string {
  switch (field) {
    case 'project':
      return projectName(item);
    case 'version':
      return versionOf(item);
    case 'selectedVersion':
      return selectedVersionOf(item);
    case 'shouldUpgrade':
      return shouldUpgradeOf(item) ? 'yes' : 'no';
    case 'recommendedVersion':
      return recommendedVersionOf(item);
    case 'eol':
    case 'incompatibleTargetKube':
    case 'outdated':
    case 'eolReason':
    case 'incompatibleTargetKubeReason':
    case 'outdatedReason': {
      const column = reasonColumn(field);
      const detail = column ? reasonDetail(item, column.key) : undefined;
      if (!detail || !column) {
        return '';
      }
      return column.kind === 'boolean'
        ? detail.upgrade_recommended
          ? 'yes'
          : 'no'
        : publicReasonText(detail);
    }
    default:
      return projectName(item);
  }
}

function downloadTextFile(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function validateSelections(content: string): UpgradeSelections {
  return yaml.load(content) as UpgradeSelections;
}

function SelectionEditDialog(props: {
  row: ProjectVersionRow;
  onDismiss: () => void;
  onConfirm: (draft: SelectionDraft) => void;
}): JSX.Element {
  const initial = draftFromRow(props.row);
  const [version, setVersion] = useState(initial.version);
  const [selectedVersion, setSelectedVersion] = useState(
    initial.selectedVersion,
  );
  const [shouldUpgrade, setShouldUpgrade] = useState(initial.shouldUpgrade);
  const [recommendedVersion, setRecommendedVersion] = useState(
    initial.recommendedVersion,
  );
  const [reasons, setReasons] = useState(initial.reasons);
  const project = projectName(props.row) || 'selection';

  return (
    <Modal
      visible
      size="large"
      header={`Edit ${project}`}
      onDismiss={props.onDismiss}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={props.onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                props.onConfirm({
                  version,
                  selectedVersion,
                  shouldUpgrade,
                  recommendedVersion,
                  reasons,
                })
              }
            >
              OK
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <ColumnLayout columns={2}>
          <FormField label="Version">
            <Input
              value={version}
              onChange={({ detail }) => setVersion(detail.value)}
            />
          </FormField>
          <FormField label="Selected version">
            <Input
              value={selectedVersion}
              onChange={({ detail }) => setSelectedVersion(detail.value)}
            />
          </FormField>
          <FormField label="Should upgrade">
            <Select
              selectedOption={yesNoOption(shouldUpgrade)}
              onChange={({ detail }) =>
                setShouldUpgrade(detail.selectedOption?.value === 'true')
              }
              options={YES_NO_OPTIONS}
            />
          </FormField>
          <FormField label="Recommended version">
            <Input
              value={recommendedVersion}
              onChange={({ detail }) => setRecommendedVersion(detail.value)}
            />
          </FormField>
        </ColumnLayout>
        {REASON_COLUMNS.filter((column) => column.kind === 'boolean').map(
          (column) => {
            const noteHeader = REASON_COLUMNS.find(
              (entry) => entry.key === column.key && entry.kind === 'reason',
            )?.header;
            return (
              <ColumnLayout columns={2} key={column.key}>
                <FormField label={column.header}>
                  <Select
                    selectedOption={yesNoOption(
                      reasons[column.key]?.upgradeRecommended,
                    )}
                    placeholder="Select"
                    onChange={({ detail }) =>
                      setReasons((current) => ({
                        ...current,
                        [column.key]: {
                          ...current[column.key],
                          upgradeRecommended:
                            detail.selectedOption?.value === 'true',
                        },
                      }))
                    }
                    options={YES_NO_OPTIONS}
                  />
                </FormField>
                <FormField label={noteHeader ?? `${column.header} reason`}>
                  <Textarea
                    value={reasons[column.key]?.note ?? ''}
                    onChange={({ detail }) =>
                      setReasons((current) => ({
                        ...current,
                        [column.key]: {
                          ...current[column.key],
                          note: detail.value,
                        },
                      }))
                    }
                  />
                </FormField>
              </ColumnLayout>
            );
          },
        )}
      </SpaceBetween>
    </Modal>
  );
}

export interface SelectionsSectionProps {
  accountId: string;
  upgradeTemplateId: string;
  showFileActions?: boolean;
  onUploaded?: () => void;
}

export default function SelectionsSection(
  props: SelectionsSectionProps,
): JSX.Element {
  const { data, error, isLoading, mutate } = useGetUpgradeTemplateSelections(
    props.accountId,
    props.upgradeTemplateId,
  );
  const [document, setDocument] = useState<SelectionsDocument | undefined>();
  const [rows, setRows] = useState<ProjectVersionRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const editingRef = useRef(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectionsFile, setSelectionsFile] = useState<File[]>([]);
  const [filter, setFilter] = useState('');
  const [sortingDescending, setSortingDescending] = useState(false);
  const [sortingColumn, setSortingColumn] = useState<
    TableProps.SortingColumn<ProjectVersionRow>
  >({ sortingField: 'project' });
  const [statusItems, setStatusItems] = useState<
    FlashbarProps.MessageDefinition[]
  >([]);

  useEffect(() => {
    setStatusItems([]);
    dirtyRef.current = false;
    editingRef.current = false;
    setEditingIndex(null);
    setDirty(false);
  }, [props.accountId, props.upgradeTemplateId]);

  useEffect(() => {
    if (!data || dirtyRef.current || editingRef.current) {
      return;
    }
    const nextDocument = data as unknown as SelectionsDocument;
    setDocument(nextDocument);
    setRows(extractProjectVersions(nextDocument).map((row) => ({ ...row })));
  }, [data]);

  const query = filter.trim().toLowerCase();
  const filteredRows = rows.filter((row) => {
    if (!query) {
      return true;
    }
    return [
      projectName(row),
      versionOf(row),
      selectedVersionOf(row),
      shouldUpgradeOf(row) ? 'yes' : 'no',
      recommendedVersionOf(row),
      ...REASON_COLUMNS.filter((column) => column.kind === 'boolean').flatMap(
        (column) => {
          const detail = reasonDetail(row, column.key);
          if (!detail) {
            return [];
          }
          return [
            detail.upgrade_recommended ? 'yes' : 'no',
            publicReasonText(detail),
          ];
        },
      ),
    ]
      .join(' ')
      .toLowerCase()
      .includes(query);
  });
  const displayedRows = [...filteredRows].sort((left, right) => {
    const comparison = sortValue(
      left,
      sortingColumn.sortingField,
    ).localeCompare(sortValue(right, sortingColumn.sortingField), undefined, {
      numeric: true,
    });
    return sortingDescending ? -comparison : comparison;
  });

  const handleSave = async () => {
    if (!document) {
      return;
    }
    setSaving(true);
    try {
      await updateUpgradeTemplateSelections(
        props.accountId,
        props.upgradeTemplateId,
        toSelectionsYamlBody(document, rows) as unknown as UpgradeSelections,
      );
      const updated = await mutate();
      if (updated) {
        const nextDocument = updated as unknown as SelectionsDocument;
        setDocument(nextDocument);
        setRows(
          extractProjectVersions(nextDocument).map((row) => ({ ...row })),
        );
      }
      dirtyRef.current = false;
      setDirty(false);
      setStatusItems([
        {
          type: 'success',
          dismissible: true,
          dismissLabel: 'Dismiss',
          content: 'Selections updated successfully',
          id: 'selections-save-success',
          onDismiss: () => setStatusItems([]),
        },
      ]);
    } catch (err: unknown) {
      setStatusItems([
        {
          type: 'error',
          dismissible: true,
          dismissLabel: 'Dismiss',
          content: saveErrorMessage(err),
          id: 'selections-save-error',
          onDismiss: () => setStatusItems([]),
        },
      ]);
    } finally {
      setSaving(false);
    }
  };

  const closeEditor = () => {
    editingRef.current = false;
    setEditingIndex(null);
  };

  const confirmEditor = (draft: SelectionDraft) => {
    if (editingIndex === null) {
      closeEditor();
      return;
    }
    const row = rows[editingIndex];
    if (row && applyDraft(row, draft)) {
      dirtyRef.current = true;
      setDirty(true);
      setRows((current) => [...current]);
    }
    closeEditor();
  };

  return (
    <ExpandableSection
      variant="container"
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
      headerText="Selections"
      headerCounter={`(${rows.length})`}
      headerDescription="Project version selections. Saving updates these rows and keeps component and package selections unchanged."
      headerActions={
        <SpaceBetween direction="horizontal" size="xs">
          {props.showFileActions && (
            <Button
              iconName="download"
              loading={downloading}
              onClick={() => {
                void (async () => {
                  setDownloading(true);
                  try {
                    const selections = await getUpgradeTemplateSelections(
                      props.accountId,
                      props.upgradeTemplateId,
                    );
                    downloadTextFile(
                      `${props.upgradeTemplateId}-selections.yaml`,
                      yaml.dump(selectionsDownloadBody(selections), {
                        noRefs: true,
                        lineWidth: -1,
                      }),
                      'application/x-yaml',
                    );
                  } catch (err: unknown) {
                    setStatusItems(
                      handleUpgradeError(err, {
                        context: 'download selections',
                      }).flashbarItems.map((item) => ({
                        ...item,
                        onDismiss: () => setStatusItems([]),
                      })),
                    );
                  } finally {
                    setDownloading(false);
                  }
                })();
              }}
            >
              Download Selections YAML
            </Button>
          )}
          {props.showFileActions && (
            <FileInput
              value={selectionsFile}
              onChange={({
                detail,
              }: {
                detail: FileInputProps.ChangeDetail;
              }) => {
                setSelectionsFile(detail.value);
                const fileReader = new FileReader();
                fileReader.onloadend = async () => {
                  try {
                    const content =
                      (fileReader.result as string | undefined) || '';
                    const selections = validateSelections(content);
                    await updateUpgradeTemplateSelections(
                      props.accountId,
                      props.upgradeTemplateId,
                      selections,
                    );
                    await mutate();
                    props.onUploaded?.();
                    setStatusItems([
                      {
                        type: 'success',
                        dismissible: true,
                        dismissLabel: 'Dismiss',
                        content: 'Selections uploaded successfully',
                        id: 'selections-upload-success',
                        onDismiss: () => setStatusItems([]),
                      },
                    ]);
                  } catch (err: unknown) {
                    setStatusItems(
                      handleUpgradeError(err, {
                        context: 'upload selections',
                      }).flashbarItems.map((item) => ({
                        ...item,
                        onDismiss: () => setStatusItems([]),
                      })),
                    );
                  }
                };
                fileReader.readAsText(detail.value[0], 'UTF-8');
              }}
            >
              Upload Selections
            </FileInput>
          )}
          <Button
            variant="primary"
            loading={saving}
            disabled={!dirty || saving || !document}
            onClick={() => void handleSave()}
          >
            Save
          </Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="s">
        {statusItems.length > 0 && <Flashbar items={statusItems} />}
        {isLoading && !data ? (
          <Spinner />
        ) : error && isNotFound(error) ? (
          <Box textAlign="center" color="inherit">
            <b>No selections</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              {selectionsLoadMessage(error)}
            </Box>
          </Box>
        ) : error ? (
          <Box color="text-status-error">{selectionsLoadMessage(error)}</Box>
        ) : (
          expanded && (
            <Table<ProjectVersionRow>
              variant="embedded"
              wrapLines
              stripedRows
              resizableColumns
              sortingColumn={sortingColumn}
              sortingDescending={sortingDescending}
              onSortingChange={({ detail }) => {
                setSortingColumn(detail.sortingColumn);
                setSortingDescending(detail.isDescending ?? false);
              }}
              filter={
                <TextFilter
                  filteringText={filter}
                  filteringPlaceholder="Find projects"
                  filteringAriaLabel="Filter project selections"
                  countText={`${displayedRows.length} ${
                    displayedRows.length === 1 ? 'match' : 'matches'
                  }`}
                  onChange={({ detail }) => setFilter(detail.filteringText)}
                />
              }
              columnDefinitions={[
                {
                  id: 'project',
                  header: 'Project',
                  cell: (item) => projectName(item) || '-',
                  sortingField: 'project',
                },
                {
                  id: 'version',
                  header: 'Version',
                  cell: (item) => versionOf(item) || '-',
                  sortingField: 'version',
                },
                {
                  id: 'selectedVersion',
                  header: 'Selected version',
                  cell: (item) => selectedVersionOf(item) || '-',
                  sortingField: 'selectedVersion',
                },
                {
                  id: 'shouldUpgrade',
                  header: 'Should upgrade',
                  cell: (item) => (shouldUpgradeOf(item) ? 'Yes' : 'No'),
                  sortingField: 'shouldUpgrade',
                },
                {
                  id: 'recommendedVersion',
                  header: 'Recommended version',
                  cell: (item) => recommendedVersionOf(item),
                  sortingField: 'recommendedVersion',
                },
                ...REASON_COLUMNS.map((column) =>
                  column.kind === 'boolean'
                    ? {
                        id: column.id,
                        header: column.header,
                        sortingField: column.id,
                        cell: (item: ProjectVersionRow) => {
                          const detail = reasonDetail(item, column.key);
                          if (!detail) {
                            return '-';
                          }
                          return detail.upgrade_recommended ? 'Yes' : 'No';
                        },
                      }
                    : {
                        id: column.id,
                        header: column.header,
                        sortingField: column.id,
                        cell: (item: ProjectVersionRow) =>
                          publicReasonText(reasonDetail(item, column.key)) ||
                          '-',
                      },
                ),
                {
                  id: 'edit',
                  header: 'Edit',
                  width: 70,
                  cell: (item) => (
                    <Button
                      iconName="edit"
                      variant="icon"
                      ariaLabel={`Edit ${projectName(item) || 'selection'}`}
                      onClick={() => {
                        const index = rows.indexOf(item);
                        if (index < 0) {
                          return;
                        }
                        editingRef.current = true;
                        setEditingIndex(index);
                      }}
                    />
                  ),
                },
              ]}
              items={displayedRows}
              loadingText="Loading selections"
              empty={
                <Box
                  margin={{ vertical: 'xs' }}
                  textAlign="center"
                  color="inherit"
                >
                  <b>No project selections</b>
                </Box>
              }
            />
          )
        )}
        {editingIndex !== null && rows[editingIndex] && (
          <SelectionEditDialog
            row={rows[editingIndex]}
            onDismiss={closeEditor}
            onConfirm={confirmEditor}
          />
        )}
      </SpaceBetween>
    </ExpandableSection>
  );
}
